/**
 * @tennis-matches/core/ko
 * K.O.-Paarungslogik für Team-basierte Doppel-Turniere (Schleifchen, Clubmeisterschaft).
 *
 * TEAM-BILDUNG (aus Rangliste):
 * - Team 1 = Spieler (1 & 2)
 * - Team 2 = Spieler (3 & 4)
 * - Team 3 = Spieler (5 & 6)
 * - Team N = Spieler ((2N-1) & 2N)
 *
 * K.O.-PAARUNGEN (bestes Team vs schlechtestes):
 * - VF (8 Teams): T1 vs T8, T2 vs T7, T3 vs T6, T4 vs T5
 * - HF (4 Teams): T1 vs T4, T2 vs T3
 * - HF (nach VF): Sieger VF1 vs Sieger VF4, Sieger VF2 vs Sieger VF3
 * - Finale: Sieger HF1 vs Sieger HF2 (genau 1 Match)
 * - Platz 3: Verlierer HF1 vs Verlierer HF2 (genau 1 Match)
 *
 * PLATZHALTER (ko-rank n):
 * - ko-rank n = Team n = Spieler ((2n-1) & 2n)
 * - Beispiel: ko-rank 1 = Team 1 = (1&2), ko-rank 8 = Team 8 = (15&16)
 */

import { KO_PLACEHOLDER } from "./ko.types.js";

/**
 * Erzeugt Viertelfinale-Paarungen (8 Teams = 16 Spieler).
 * VF1: Team 1 (1&2) vs Team 8 (15&16)
 * VF2: Team 2 (3&4) vs Team 7 (13&14)
 * VF3: Team 3 (5&6) vs Team 6 (11&12)
 * VF4: Team 4 (7&8) vs Team 5 (9&10)
 * @param {number} roundIndex - 0-basierter Rundenindex
 * @param {Function} makeMatchId - (r, m) => string
 * @param {number[]} courts - Court-Nummern
 * @returns {object[]} 4 Matches
 */
export function buildViertelfinale(roundIndex, makeMatchId, courts = [1, 2, 3, 4]) {
  return [
    { type: "doubles", A: [KO_PLACEHOLDER.RANK, 1], B: [KO_PLACEHOLDER.RANK, 8], id: makeMatchId(roundIndex, 0), court: courts[0] ?? 1 },
    { type: "doubles", A: [KO_PLACEHOLDER.RANK, 2], B: [KO_PLACEHOLDER.RANK, 7], id: makeMatchId(roundIndex, 1), court: courts[1] ?? 2 },
    { type: "doubles", A: [KO_PLACEHOLDER.RANK, 3], B: [KO_PLACEHOLDER.RANK, 6], id: makeMatchId(roundIndex, 2), court: courts[2] ?? 3 },
    { type: "doubles", A: [KO_PLACEHOLDER.RANK, 4], B: [KO_PLACEHOLDER.RANK, 5], id: makeMatchId(roundIndex, 3), court: courts[3] ?? 4 },
  ];
}

/**
 * Erzeugt Halbfinale-Paarungen (4 Teams = 8 Spieler).
 * HF1: Team 1 (1&2) vs Team 4 (7&8)
 * HF2: Team 2 (3&4) vs Team 3 (5&6)
 * @param {number} roundIndex
 * @param {Function} makeMatchId
 * @param {number[]} courts
 * @returns {object[]} 2 Matches
 */
export function buildHalbfinale4(roundIndex, makeMatchId, courts = [1, 2]) {
  return [
    { type: "doubles", A: [KO_PLACEHOLDER.RANK, 1], B: [KO_PLACEHOLDER.RANK, 4], id: makeMatchId(roundIndex, 0), court: courts[0] ?? 1 },
    { type: "doubles", A: [KO_PLACEHOLDER.RANK, 2], B: [KO_PLACEHOLDER.RANK, 3], id: makeMatchId(roundIndex, 1), court: courts[1] ?? 2 },
  ];
}

/**
 * Erzeugt Halbfinale-Paarungen (8 Teams, nach VF).
 * HF1: Sieger VF1 vs Sieger VF4, HF2: Sieger VF2 vs Sieger VF3
 * @param {number} roundIndex
 * @param {Function} makeMatchId
 * @param {number[]} courts
 * @returns {object[]}
 */
export function buildHalbfinale8(roundIndex, makeMatchId, courts = [1, 2]) {
  return [
    { type: "doubles", A: [KO_PLACEHOLDER.WINNER_VF, 1], B: [KO_PLACEHOLDER.WINNER_VF, 4], id: makeMatchId(roundIndex, 0), court: courts[0] ?? 1 },
    { type: "doubles", A: [KO_PLACEHOLDER.WINNER_VF, 2], B: [KO_PLACEHOLDER.WINNER_VF, 3], id: makeMatchId(roundIndex, 1), court: courts[1] ?? 2 },
  ];
}

/**
 * Erzeugt Finale – genau ein Match.
 * Sieger HF1 vs Sieger HF2
 * @param {number} roundIndex
 * @param {Function} makeMatchId
 * @param {number} court
 * @returns {object[]}
 */
export function buildFinale(roundIndex, makeMatchId, court = 1) {
  return [
    { type: "doubles", A: [KO_PLACEHOLDER.WINNER_HF, 1], B: [KO_PLACEHOLDER.WINNER_HF, 2], id: makeMatchId(roundIndex, 0), court },
  ];
}

/**
 * Erzeugt Platz-3-Match – Verlierer HF1 vs Verlierer HF2 (genau 1 Match).
 * @param {number} roundIndex
 * @param {Function} makeMatchId
 * @param {number} matchIndex - Match-Index in der Runde (für Match-ID)
 * @param {number} court
 * @returns {object} Single match object (not array!)
 */
export function buildPlatz3Match(roundIndex, makeMatchId, matchIndex, court = 2) {
  return { type: "doubles", A: [KO_PLACEHOLDER.LOSER_HF, 1], B: [KO_PLACEHOLDER.LOSER_HF, 2], id: makeMatchId(roundIndex, matchIndex), court };
}

/**
 * Erzeugt Finale-Runde mit Finale + Platz 3 (2 Matches in einer Runde).
 * Match 1: Finale (Sieger HF1 vs Sieger HF2)
 * Match 2: Platz 3 (Verlierer HF1 vs Verlierer HF2)
 * @param {number} roundIndex
 * @param {Function} makeMatchId
 * @param {number[]} courts
 * @returns {object[]} 2 Matches
 */
export function buildFinaleRunde(roundIndex, makeMatchId, courts = [1, 2]) {
  return [
    { type: "doubles", A: [KO_PLACEHOLDER.WINNER_HF, 1], B: [KO_PLACEHOLDER.WINNER_HF, 2], id: makeMatchId(roundIndex, 0), court: courts[0] ?? 1 },
    { type: "doubles", A: [KO_PLACEHOLDER.LOSER_HF, 1], B: [KO_PLACEHOLDER.LOSER_HF, 2], id: makeMatchId(roundIndex, 1), court: courts[1] ?? 2 },
  ];
}
